/**
 * Weak Points Tab
 * Cross-course pull of items with retrievability < 50% (or lapses ≥ 3) — the items
 * the learner is on the verge of forgetting. One-click into a focused practice session.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import reviewCenterService, {
  ReviewItemSummary,
} from '../../../services/reviewCenterService';
import './WeakPointsTab.css';

const WEAK_THRESHOLD = 0.5;
const SESSION_CAP = 30;

const WeakPointsTab: React.FC = () => {
  const { t } = useTranslation('review');
  const navigate = useNavigate();
  const [items, setItems] = useState<ReviewItemSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    reviewCenterService
      .getCustomItems({
        scopeMode: 'all',
        types: ['vocabulary', 'knowledgePoint', 'pattern'],
        retrievabilityMax: WEAK_THRESHOLD,
      })
      .then(data => {
        if (!cancelled) setItems(data.items);
      })
      .catch(err => {
        if (!cancelled) setError(err?.message || t('weak.loadError'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  const breakdown = useMemo(() => {
    const counts = { vocabulary: 0, knowledgePoint: 0, pattern: 0 };
    for (const item of items) counts[item.type]++;
    return counts;
  }, [items]);

  const handleStart = () => {
    if (items.length === 0) return;
    const top = items.slice(0, SESSION_CAP);
    const params = new URLSearchParams();
    const vocabIds = top.filter(i => i.type === 'vocabulary').map(i => i.id);
    const kpIds = top.filter(i => i.type === 'knowledgePoint').map(i => i.id);
    const patternIds = top.filter(i => i.type === 'pattern').map(i => i.id);
    if (vocabIds.length) params.set('vocabIds', vocabIds.join(','));
    if (kpIds.length) params.set('kpIds', kpIds.join(','));
    if (patternIds.length) params.set('patternIds', patternIds.join(','));
    params.set('mode', 'smart');
    params.set('count', String(top.length));
    params.set('stages', 'meaning,fillBlank,dictation,speaking');
    navigate(`/vocabulary-book/practice?${params.toString()}`);
  };

  if (loading) {
    return (
      <div className="rc-loading">
        <div className="rc-spinner" aria-hidden="true" />
        <p>{t('weak.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rc-error">
        <p>{error}</p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="rc-tab-empty">
        <p>{t('weak.empty')}</p>
        <p className="rc-today__sub">{t('weak.emptySub')}</p>
      </div>
    );
  }

  return (
    <div className="rc-weak">
      <div className="rc-section">
        <h3 className="rc-section__title">{t('weak.summaryTitle', { count: items.length })}</h3>
        <p className="rc-weak__hint">{t('weak.thresholdHint', { pct: WEAK_THRESHOLD * 100 })}</p>
        <div className="rc-weak__breakdown">
          {breakdown.vocabulary > 0 && (
            <span className="rc-type-tag rc-type-vocab">
              {t('itemType.vocabulary')} {breakdown.vocabulary}
            </span>
          )}
          {breakdown.knowledgePoint > 0 && (
            <span className="rc-type-tag rc-type-kp">
              {t('itemType.knowledgePoint')} {breakdown.knowledgePoint}
            </span>
          )}
          {breakdown.pattern > 0 && (
            <span className="rc-type-tag rc-type-pattern">
              {t('itemType.pattern')} {breakdown.pattern}
            </span>
          )}
        </div>

        <button
          className="rc-start-btn rc-weak__cta"
          onClick={handleStart}
        >
          {t('weak.startBtn', { count: Math.min(items.length, SESSION_CAP) })}
        </button>
      </div>

      <div className="rc-section">
        <h3 className="rc-section__title">{t('weak.previewTitle')}</h3>
        <div className="rc-items">
          {items.slice(0, SESSION_CAP).map(item => (
            <div key={`${item.type}-${item.id}`} className="rc-item-row">
              <span className={`rc-type-tag rc-type-${item.type === 'vocabulary' ? 'vocab' : item.type === 'knowledgePoint' ? 'kp' : 'pattern'}`}>
                {t(`itemType.${item.type}`)}
              </span>
              <span className="rc-item-term">{item.term}</span>
              <span className="rc-item-r" style={{ color: '#dc2626' }}>
                {((item.retrievability ?? 0) * 100).toFixed(0)}%
              </span>
            </div>
          ))}
        </div>
        {items.length > SESSION_CAP && (
          <p className="rc-empty-hint">{t('weak.moreItems', { count: items.length - SESSION_CAP })}</p>
        )}
      </div>
    </div>
  );
};

export default WeakPointsTab;
